/**
 * useDeleteVolume Hook
 *
 * Custom React Query mutation hook for deleting volume resources.
 * IMPORTANT: Volume can only be deleted if it has no access grants.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { volumeService } from '../../../api/volume.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseDeleteVolumeOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for deleting volume
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useDeleteVolume = (tenantId: string, options?: UseDeleteVolumeOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (volumeId: string) => volumeService.deleteVolume(tenantId, volumeId),
    onSuccess: () => {
      // Invalidate volume list query
      queryClient.invalidateQueries({ queryKey: ['volumes', tenantId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
